import { SideDrawer } from '@stern-ui/ui';
import { Close, Menu } from 'mdi-material-ui';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { useFetchUserQuery } from '@/api';
import { MenuItem } from '@/components/Elements/MenuItem';
import { resetState } from '@/components/PatientDataTable/store/dataTableSlice';
import { MenuConfigObject, MenuConfigItem } from '@/config/menuConfig';
import { roles, SIDE_NAVIGATION_MENU } from '@/globalConsts';
import { useUserRole } from '@/hooks';
import { trackEvent } from '@/lib/analytics';

export interface DrawerProps {
	className?: string;
	close: () => void;
	config: MenuConfigObject;
	isOpen: boolean;
	open: () => void;
}

export const Drawer = (props: DrawerProps) => {
	const { className, close, config, isOpen, open } = props;

	const dispatch = useDispatch();
	const { data: userData } = useFetchUserQuery();
	const userFeatures = userData?.features;
	const userRole = useUserRole(userFeatures);

	const handleClick = () => {
		dispatch(resetState());
		trackEvent('Sidebar menu item clicked');
	};

	const doShowHome = useMemo(() => {
		// Only users with care_agent' or 'study_site_admin' roles require the Home menu item
		if (
			(userFeatures && userRole === roles.CARE_AGENT) ||
			(userFeatures && userRole === roles.STUDY_SITE_ADMIN)
		)
			return true;

		return false;
	}, [userFeatures, userRole]);

	const renderDashboardItems = (menu: MenuConfigItem[]) => {
		return menu.map((dashboardItem, i) => {
			const { icon, link, title, testId } = dashboardItem;

			return (
				<NavLink
					key={i}
					to={link}
					title={title}
					children={({ isActive }) => {
						return (
							<MenuItem
								isExpanded={isOpen}
								icon={icon.nav}
								iconActive={icon.navActive}
								iconHover={icon.navHover}
								isActive={isActive}
								className='mt-2'
								onClick={!isActive ? handleClick : undefined}
							>
								{title}
							</MenuItem>
						);
					}}
					data-test-id={`${testId}-menu-item`}
				></NavLink>
			);
		});
	};

	const handleButtonClick = () => {
		if (isOpen) {
			close();
			trackEvent('Sidebar menu closed');
		} else {
			open();
			trackEvent('Sidebar menu expanded');
		}
	};

	return (
		<SideDrawer
			isExpanded={isOpen}
			containerClass={`bg-primaryBackground h-screen min-h-screen pt-5 overflow-x-hidden z-30 ${isOpen ? `${SIDE_NAVIGATION_MENU.WIDTH.EXPANDED}` : `${SIDE_NAVIGATION_MENU.WIDTH.DEFAULT}`} ${className}`}
			buttonClass='top-[1.25rem] right-[0.75rem]'
			handleClick={handleButtonClick}
			name='Main menu'
			openIcon={<Menu className='text-secondaryTextDark' />}
			closeIcon={<Close className='text-secondaryTextDark' />}
			dataTestId='side-navigation-menu'
		>
			<nav className='mx-2 mt-16'>
				{doShowHome && renderDashboardItems(config.home)}
				{userFeatures &&
					userRole === roles.CARE_AGENT &&
					renderDashboardItems(config.care_agent)}
				{userFeatures &&
					userRole === roles.STUDY_SITE_ADMIN &&
					renderDashboardItems(config.study_site_admin)}
			</nav>
		</SideDrawer>
	);
};
